import { routes, type Lang } from "@/utils/routes";

export type AnnouncementFamily =
  | "home"
  | "product"
  | "solution"
  | "comparison"
  | "editorial";

export interface AnnouncementCampaign {
  id: "free-analysis-2026-09" | "mcp-2026-09" | "saivo-investment-2026-10";
  enabled: boolean;
  startsAt: string;
  endsAt: string;
  destination:
    | "freeAnalysis"
    | "articles.aiAssistantMcp"
    | "articles.saivoInvestment";
  families: readonly AnnouncementFamily[];
  copy: Record<Lang, { title: string; action: string; dismiss: string }>;
}

/** One repo-managed campaign. End is exclusive: 30 days in Europe/Stockholm. */
export const announcementCampaign: AnnouncementCampaign = {
  id: "saivo-investment-2026-10",
  enabled: true,
  startsAt: "2026-10-27T00:00:00+01:00",
  endsAt: "2026-11-26T00:00:00+01:00",
  destination: "articles.saivoInvestment",
  families: ["home", "product", "solution", "comparison", "editorial"],
  copy: {
    sv: {
      title: "Saivo investerar i Caire",
      action: "Läs pressmeddelandet",
      dismiss: "Stäng meddelandet om Saivos investering",
    },
    en: {
      title: "Saivo invests in Caire",
      action: "Read the press release",
      dismiss: "Dismiss the Saivo investment announcement",
    },
  },
};

/** Match known routes only. Help, legal, booking and unknown pages fail closed. */
export function announcementFamily(
  pathname: string,
): AnnouncementFamily | null {
  const path = pathname.replace(/\/$/, "") || "/";
  const matches = (pairs: readonly (readonly string[])[]) =>
    pairs.some((pair) => pair.includes(path));
  if (matches([routes.home])) return "home";
  if (
    matches([
      routes.product.interactiveDemo,
      routes.product.dashboardTour,
      routes.product.mobileAppTour,
      routes.product.film,
    ])
  )
    return null;
  if (matches(Object.values(routes.solutions))) return "solution";
  if (matches(Object.values(routes.comparisons))) return "comparison";
  if (
    matches([
      ...Object.values(routes.features),
      ...Object.values(routes.product),
      routes.vadArCaire,
      routes.saFungerarCaire,
      routes.plattform,
    ])
  )
    return "product";
  if (
    matches([
      ...Object.values(routes.articles),
      ...Object.values(routes.guides),
      ...Object.values(routes.resourcesCategory),
    ])
  )
    return "editorial";
  return null;
}

export function isAnnouncementActive(
  campaign: AnnouncementCampaign,
  pathname: string,
  now: number,
): boolean {
  const family = announcementFamily(pathname);
  return Boolean(
    campaign.enabled &&
    family &&
    campaign.families.includes(family) &&
    now >= Date.parse(campaign.startsAt) &&
    now < Date.parse(campaign.endsAt),
  );
}
